<script>
  import { onDestroy, tick } from 'svelte';
  import { SmilePlus } from 'lucide-svelte';
  import ReactionPopup from './ReactionPopup.svelte';
  import { lastSelectedReaction } from '$lib/stores/store.js';

  const DEFAULT_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '😡'];
  const POPUP_GAP = 10;
  const POPUP_VERTICAL_OFFSET = 0;
  const POPUP_PADDING = 12;
  const HOVER_OPEN_DELAY = 90;
  const HOVER_CLOSE_DELAY = 180;
  const LONG_PRESS_DELAY = 500;

  export let fromMe = false;
  export let messageId;
  export let reactions = DEFAULT_REACTIONS;
  export let currentReaction = null;
  export let interactionMode = 'desktop';
  export let onSelect = () => {};
  export let onOpenChange = () => {};

  let rootEl;
  let triggerEl;
  let popupEl;
  let open = false;
  let popupPos = { x: 0, y: 0 };
  let popupSide = fromMe ? 'right' : 'left';
  let pointerOverTrigger = false;
  let pointerOverPopup = false;
  let hoverOpenTimer = null;
  let hoverCloseTimer = null;
  let listenersBound = false;
  let longPressTimer = null;
  let longPressArmed = false;
  let suppressClickAfterLongPress = false;

  $: reactionOptions = Array.isArray(reactions) && reactions.length ? reactions : DEFAULT_REACTIONS;
  $: activeReaction = typeof currentReaction === 'string' && currentReaction.trim() ? currentReaction : null;
  $: defaultReaction = $lastSelectedReaction || reactionOptions.at(0) || DEFAULT_REACTIONS.at(0);
  $: if (open) {
    bindOpenListeners();
    void syncPopupPosition();
  } else {
    unbindOpenListeners();
  }

  function clearHoverTimers() {
    clearTimeout(hoverOpenTimer);
    clearTimeout(hoverCloseTimer);
    hoverOpenTimer = null;
    hoverCloseTimer = null;
  }

  function clearLongPressTimer() {
    clearTimeout(longPressTimer);
    longPressTimer = null;
    longPressArmed = false;
  }

  function getChatBounds() {
    const boundsEl = triggerEl?.closest('.message-scroll') || triggerEl?.closest('.message-viewport');
    const rect = boundsEl?.getBoundingClientRect();

    if (rect) {
      return {
        left: rect.left,
        right: rect.right,
        top: rect.top,
        bottom: rect.bottom
      };
    }

    return {
      left: POPUP_PADDING,
      right: window.innerWidth - POPUP_PADDING,
      top: POPUP_PADDING,
      bottom: window.innerHeight - POPUP_PADDING
    };
  }

  async function syncPopupPosition() {
    if (!triggerEl) return;

    await tick();

    const triggerRect = triggerEl.getBoundingClientRect();
    const bounds = getChatBounds();
    const width = popupEl?.offsetWidth ?? 308;
    const height = popupEl?.offsetHeight ?? 60;
    popupSide = fromMe ? 'right' : 'left';

    const edgeGap = 4;
    const xRaw = fromMe ? window.innerWidth - triggerRect.right + edgeGap : triggerRect.left - edgeGap;
    const xMax = Math.max(POPUP_PADDING, window.innerWidth - width - POPUP_PADDING);
    const x = Math.max(POPUP_PADDING, Math.min(xRaw, xMax));

    let y = triggerRect.top - height - POPUP_VERTICAL_OFFSET;
    if (y < bounds.top + POPUP_PADDING) {
      y = Math.min(bounds.bottom - height - POPUP_PADDING, triggerRect.bottom + POPUP_GAP);
    }

    popupPos = { x, y };
  }

  function bindOpenListeners() {
    if (listenersBound) return;

    window.addEventListener('pointerdown', handleOutsidePointer, true);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    listenersBound = true;
  }

  function unbindOpenListeners() {
    if (!listenersBound) return;

    window.removeEventListener('pointerdown', handleOutsidePointer, true);
    window.removeEventListener('resize', handleViewportChange);
    window.removeEventListener('scroll', handleViewportChange, true);
    listenersBound = false;
  }

  function openPicker() {
    clearHoverTimers();
    clearLongPressTimer();
    if (open) return;
    open = true;
    onOpenChange(true);
  }

  function closePicker(force = false) {
    clearHoverTimers();
    clearLongPressTimer();

    if (!force && (pointerOverTrigger || pointerOverPopup)) return;
    if (!open) return;
    open = false;
    onOpenChange(false);
  }

  function scheduleHoverOpen() {
    if (interactionMode === 'mobile') return;

    clearTimeout(hoverOpenTimer);
    clearTimeout(hoverCloseTimer);
    hoverOpenTimer = setTimeout(() => {
      openPicker();
    }, HOVER_OPEN_DELAY);
  }

  function scheduleHoverClose() {
    clearTimeout(hoverOpenTimer);

    if (interactionMode === 'mobile') return;

    hoverCloseTimer = setTimeout(() => {
      closePicker();
    }, HOVER_CLOSE_DELAY);
  }

  function handleTriggerEnter() {
    pointerOverTrigger = true;
    scheduleHoverOpen();
  }

  function handleTriggerLeave() {
    pointerOverTrigger = false;
    scheduleHoverClose();
  }

  function handleTriggerPointerEnter(event) {
    event.preventDefault();
    if (event?.pointerType === 'touch') return;
    handleTriggerEnter();
  }

  function handleTriggerPointerLeave(event) {
    event.preventDefault();
    if (event?.pointerType === 'touch') return;
    handleTriggerLeave();
  }

  function handlePopupEnter() {
    pointerOverPopup = true;
    clearTimeout(hoverCloseTimer);
  }

  function handlePopupLeave() {
    pointerOverPopup = false;
    scheduleHoverClose();
  }

  function handleTriggerClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (suppressClickAfterLongPress) {
      suppressClickAfterLongPress = false;
      event.preventDefault();
      return;
    }

    selectReaction(defaultReaction);
  }

  function handleTriggerContextMenu(event) {
    event.preventDefault();
    event.stopPropagation();

    if (open) {
      closePicker(true);
      return;
    }

    openPicker();
  }

  function armLongPress(event) {
    const pointerType = event?.pointerType || (event?.type?.startsWith('touch') ? 'touch' : null);
    if (pointerType === 'mouse') return;
    if (longPressArmed) return;

    longPressArmed = true;
    longPressTimer = setTimeout(() => {
      if (!longPressArmed) return;
      suppressClickAfterLongPress = true;
      openPicker();
    }, LONG_PRESS_DELAY);
  }

  function disarmLongPress() {
    const wasArmed = longPressArmed;
    clearLongPressTimer();

    if (wasArmed && open) {
      suppressClickAfterLongPress = true;
    }
  }

  function handleTouchStart(event) {
    armLongPress(event);
  }

  function handleTouchEnd() {
    disarmLongPress();
  }

  function handleTouchCancel() {
    disarmLongPress();
  }

  function handleOutsidePointer(event) {
    if (rootEl?.contains(event.target) || popupEl?.contains(event.target)) return;
    closePicker(true);
  }

  function handleViewportChange() {
    if (!open) return;
    void syncPopupPosition();
  }

  function selectReaction(reaction) {
    lastSelectedReaction.set(reaction);
    onSelect({
      messageId,
      reaction,
      remove: false,
      previousReaction: activeReaction
    });

    closePicker(true);
  }

  function removeReaction() {
    if (!activeReaction) {
      closePicker(true);
      return;
    }

    onSelect({
      messageId,
      reaction: activeReaction,
      remove: true,
      previousReaction: activeReaction
    });

    closePicker(true);
  }

  onDestroy(() => {
    clearHoverTimers();
    clearLongPressTimer();
    unbindOpenListeners();
    onOpenChange(false);
  });
</script>

<ReactionPopup
  bind:popupEl
  {open}
  {popupPos}
  {popupSide}
  {reactionOptions}
  {activeReaction}
  {interactionMode}
  onEnter={handlePopupEnter}
  onLeave={handlePopupLeave}
  onSelect={selectReaction}
  onRemove={removeReaction} />

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  bind:this={rootEl}
  class="reaction-root"
  on:mouseenter={handleTriggerEnter}
  on:mouseleave={handleTriggerLeave}
  on:pointerenter={handleTriggerPointerEnter}
  on:pointerleave={handleTriggerPointerLeave}
  on:pointerdown|stopPropagation={armLongPress}
  on:pointerup|stopPropagation={handleTouchEnd}
  on:pointercancel|stopPropagation={handleTouchCancel}
  on:touchstart|stopPropagation={handleTouchStart}
  on:touchend|stopPropagation={handleTouchEnd}
  on:touchcancel|stopPropagation={handleTouchCancel}
  on:contextmenu|preventDefault|stopPropagation={handleTriggerContextMenu}>
  <button
    bind:this={triggerEl}
    type="button"
    class="reaction-trigger"
    class:reaction-trigger--active={!!activeReaction}
    class:reaction-trigger--mobile={interactionMode === 'mobile'}
    aria-label={activeReaction ? `Thả thêm cảm xúc, hiện tại ${activeReaction}` : 'Thả cảm xúc'}
    aria-expanded={open}
    on:pointerdown|stopPropagation
    on:pointerup|stopPropagation
    on:touchstart|stopPropagation
    on:touchend|stopPropagation
    on:click|preventDefault|stopPropagation={handleTriggerClick}>
    {#if activeReaction}
      <span class="reaction-trigger__emoji" aria-hidden="true">{activeReaction}</span>
    {:else}
      <SmilePlus size={16} strokeWidth={2.1} />
    {/if}
  </button>
</div>

<style>
  .reaction-root {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.12rem;
    border-radius: 999px;
    touch-action: manipulation;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
    z-index: 1;
  }

  .reaction-trigger {
    position: relative;
    display: grid;
    place-items: center;
    width: 1.4rem;
    height: 1.4rem;
    border: 1px solid rgba(203, 213, 225, 0.88);
    border-radius: 999px;
    color: #334155;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.1);
    backdrop-filter: blur(18px);
    touch-action: manipulation;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
    z-index: 1;
    transition:
      transform 140ms ease,
      background-color 140ms ease,
      border-color 140ms ease,
      color 140ms ease,
      box-shadow 140ms ease;
    will-change: transform;
  }

  .reaction-trigger:hover,
  .reaction-trigger:focus-visible,
  .reaction-trigger--active {
    transform: translateY(-1px);
    border-color: rgba(96, 165, 250, 0.55);
    color: #1d4ed8;
    background: rgba(239, 246, 255, 0.98);
    box-shadow: 0 14px 28px rgba(37, 99, 235, 0.12);
  }

  .reaction-trigger__emoji {
    font-size: 1.1rem;
    line-height: 1;
  }

  .reaction-trigger--mobile {
    width: 1.56rem;
    height: 1.56rem;
  }

  .reaction-trigger--mobile :global(svg) {
    width: 0.8rem;
    height: 0.8rem;
  }

  .reaction-trigger--mobile .reaction-trigger__emoji {
    font-size: 0.88rem;
  }
</style>
